import {
    GridView as GridViewIcon,
    List as ListViewIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Grid,
    InputAdornment,
    Paper,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from "react";
import { FileItem, fileItemListByUpdate, FileItemListByUpdateResult, transcode } from "../Api";
import FileCard from "../components/FileCard";
import FileList, { SortDirection, SortField } from "../components/FileList";
import { useLocalStorage } from "../hooks/UseLocalStorage";

type ViewMode = 'grid' | 'list';

function smartCompareDate(i1: FileItem, i2: FileItem): number {
    if (i1.date && i2.date) {
        return i1.date.localeCompare(i2.date);
    }
    if (i1.date) {
        return 1;
    }
    if (i2.date) {
        return -1;
    }
    return (i1.upload.localeCompare(i2.upload));
}

export const Files: React.FC = () => {
    const [callLocal] = useLocalStorage("callLocal", false);
    const [viewMode, setViewMode] = useLocalStorage("filesViewMode", "list");
    const [result, setResult] = useState<FileItemListByUpdateResult>();
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState<SortField>("date");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fileItemListByUpdate(callLocal)
            .then(result => {
                setResult(result);
                setLoading(false);
            })
            .catch(err => {
                setError("Failed to load files. Please try again.");
                setLoading(false);
                console.error(err);
            });
    }, [callLocal]);

    function startTranscode(fileItem: FileItem) {
        console.log("Transcode: ", fileItem);
        transcode(callLocal, fileItem.key)
            .catch(e => console.error(e));
    }

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedItems = React.useMemo(() => {
        const items = result?.fileItemListByUpdate.fileItemList || [];
        
        // Filter items based on search term
        const filteredItems = items.filter(item => {
            const searchLower = searchTerm.toLowerCase();
            const title = (item.title || item.filename).toLowerCase();
            const filename = item.filename.toLowerCase();
            return title.includes(searchLower) || filename.includes(searchLower);
        });

        // Sort items based on selected criteria
        return [...filteredItems].sort((a, b) => {
            let comparison = 0;
            
            switch (sortField) {
                case "name":
                    comparison = (a.title || a.filename).localeCompare(b.title || b.filename);
                    break;
                case "size":
                    comparison = parseInt(a.size) - parseInt(b.size);
                    break;
                case "upload":
                    comparison = parseInt(a.upload) - parseInt(b.upload);
                    break;
                case "type":
                    const extA = a.filename.toLowerCase().split('.').pop() || '';
                    const extB = b.filename.toLowerCase().split('.').pop() || '';
                    comparison = extA.localeCompare(extB);
                    break;
                case "date":
                default:
                    comparison = smartCompareDate(a, b);
                    break;
            }
            
            return sortDirection === 'asc' ? comparison : -comparison;
        });
    }, [result, searchTerm, sortField, sortDirection]);

    const getFileTypeStats = () => {
        const stats: { [key: string]: number } = {};
        const allItems = result?.fileItemListByUpdate.fileItemList || [];
        allItems.forEach(item => {
            const extension = item.filename.toLowerCase().split('.').pop() || 'unknown';
            stats[extension] = (stats[extension] || 0) + 1;
        });
        return stats;
    };

    const fileTypeStats = getFileTypeStats();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom>
                    Family Files
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Browse and manage your family archive collection
                </Typography>

                {/* Stats */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Collection Overview
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip label={`Total Files: ${result?.fileItemListByUpdate.fileItemList?.length || 0}`} color="primary" />
                        {Object.entries(fileTypeStats).map(([type, count]) => (
                            <Chip
                                key={type}
                                label={`${type.toUpperCase()}: ${count}`}
                                variant="outlined"
                                size="small"
                            />
                        ))}
                    </Box>
                    {callLocal && (
                        <Chip label="Local Mode" color="warning" size="small" />
                    )}
                </Paper>

                {/* Search and View Controls */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        placeholder="Search files..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ flexGrow: 1, minWidth: 250 }}
                    />
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <ListViewIcon />
                        </ToggleButton>
                        <ToggleButton value="grid" aria-label="grid view">
                            <GridViewIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {/* Files Display */}
            {sortedItems.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {searchTerm ? 'No files match your search' : 'No files found'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'Try adjusting your search terms' : 'Upload some files to get started'}
                    </Typography>
                </Paper>
            ) : viewMode === 'list' ? (
                <FileList
                    files={sortedItems}
                    onTranscode={startTranscode}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                />
            ) : (
                <Grid container spacing={3}>
                    {sortedItems.map((fileItem) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={fileItem.key}>
                            <FileCard
                                fileItem={fileItem}
                                onTranscode={startTranscode}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
}

export default Files;
